import { useFetch } from "@/lib/axios.config";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

export interface DayInfo {
  day_number: number;
  award: string;
  date: string;
  completed: boolean;
}

export interface DailyCheckInCalendarResponse {
  days: DayInfo[];
}

export enum DailyCheckKeys {
  DailyCheckInCalendar = "daily-check-in-calendar",
}

const getDailyCheckInCalendar =
  async (): Promise<DailyCheckInCalendarResponse> => {
    const res = await useFetch.get<DailyCheckInCalendarResponse>(
      "/tasks/DailyCheckInCalendar"
    );
    return res.data;
  };

export const useDailyCheckInCalendarOptions = () => {
  return queryOptions({
    queryKey: [DailyCheckKeys.DailyCheckInCalendar],
    queryFn: getDailyCheckInCalendar,
  });
};

export interface ClaimDailyCheckInResponse {
  total_awarded_don: number;
  day_number: number;
  // Backend sets these when a non-premium user resets to day 1 after a
  // 1-day miss. The frontend uses them to open the recovery modal.
  was_reset: boolean;
  previous_day_number: number | null;
  recovery_available_until: string | null;
  recovery_requires_check_in: boolean;
  // True when premium silently caught the user up after a 1-day miss
  // (so they don't lose progress). Frontend shows StreakSavedModal.
  was_premium_grace: boolean;
}

export interface RecoverStreakResponse {
  day_number: number;
  previous_day_number: number;
  awarded_difference: string;
}

const getDailyAward = async (): Promise<ClaimDailyCheckInResponse> => {
  const res = await useFetch.post<ClaimDailyCheckInResponse>(
    "/tasks/ClaimDailyCheckIn",
    {
      timezone: "Asia/Tashkent",
    }
  );
  return res.data;
};

export const useGetDailyAwardMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: getDailyAward,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DailyCheckKeys.DailyCheckInCalendar],
      });
      queryClient.invalidateQueries({
        queryKey: ["navigation"],
      });
      queryClient.invalidateQueries({
        queryKey: ["profile"],
      });
    },
  });
};

const recoverStreak = async (): Promise<RecoverStreakResponse> => {
  const res = await useFetch.post<RecoverStreakResponse>(
    "/tasks/RecoverStreak",
    {
      timezone: "Asia/Tashkent",
    }
  );
  return res.data;
};

export const useRecoverStreakMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recoverStreak,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DailyCheckKeys.DailyCheckInCalendar],
      });
      queryClient.invalidateQueries({ queryKey: ["navigation"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
};
